/**
 * Contenuto del Glossario del portale CRA.
 *
 * Ogni voce ha una descrizione breve (una riga, per scorrere la lista) e una
 * lunga (contesto/normativa/come si usa nel portale). Testo in italiano,
 * coerente col resto della UI. Sorgente unica: modificare qui aggiorna la pagina.
 */

export interface GlossaryEntry {
  /** Sigla o termine visualizzato come titolo della voce. */
  term: string;
  /** Estensione del termine, quando è un acronimo (opzionale). */
  expansion?: string;
  /** Descrizione breve: una riga, mostrata sempre. */
  short: string;
  /** Descrizione lunga: contesto e uso nel portale. */
  long: string;
}

/** Voci del glossario, ordinate alfabeticamente per termine. */
export const GLOSSARY: GlossaryEntry[] = [
  {
    term: 'Bundle',
    short: 'Insieme di componenti di una release su cui vengono caricati i BOM.',
    long: 'Un bundle raggruppa i componenti software/hardware associati a una specifica release di prodotto. Su ogni bundle vengono caricati i BOM (SBOM/HBOM) per l’analisi delle vulnerabilità.',
  },
  {
    term: 'BOM',
    expansion: 'Bill of Materials',
    short: 'Distinta dei componenti che compongono un prodotto o una release.',
    long: 'La Bill of Materials elenca i componenti (librerie, moduli, parti hardware) di un artefatto. Nel portale si distingue tra SBOM (software) e HBOM (hardware); i BOM caricati vengono analizzati per correlare i componenti alle CVE note.',
  },
  {
    term: 'CPE',
    expansion: 'Common Platform Enumeration',
    short: 'Identificatore standard di un prodotto/piattaforma usato per il matching delle CVE.',
    long: 'La CPE è una stringa strutturata (es. cpe:2.3:h:vendor:product:version:…) che identifica in modo univoco un prodotto. Il portale la usa come prefisso a livello di prodotto e suffisso a livello di release per associare automaticamente le vulnerabilità pubblicate.',
  },
  {
    term: 'CRA',
    expansion: 'Cyber Resilience Act',
    short: 'Regolamento UE sulla sicurezza dei prodotti con elementi digitali.',
    long: 'Il Cyber Resilience Act impone obblighi di gestione delle vulnerabilità, notifica degli incidenti gravi e trasparenza sulla supply chain software. Il portale operativizza questi obblighi: tracciamento SBOM, triage CVE, report SRP e trasmissione ENISA.',
  },
  {
    term: 'CVE',
    expansion: 'Common Vulnerabilities and Exposures',
    short: 'Identificatore univoco di una vulnerabilità nota pubblicamente.',
    long: 'Una CVE (es. CVE-2024-12345) è il riferimento standard per una vulnerabilità divulgata. Il portale correla le CVE ai componenti dei prodotti tramite CPE/PURL; il triage decide poi se ogni CVE è applicabile e con quale impatto.',
  },
  {
    term: 'CVSS',
    expansion: 'Common Vulnerability Scoring System',
    short: 'Punteggio di gravità di una vulnerabilità, da 0.0 a 10.0.',
    long: 'Il CVSS assegna a una CVE un punteggio di severità (Low/Medium/High/Critical) a partire da metriche di sfruttabilità e impatto. Nel portale è uno dei criteri di prioritizzazione del triage e delle soglie SLA.',
  },
  {
    term: 'ENISA',
    expansion: 'European Union Agency for Cybersecurity',
    short: 'Agenzia UE destinataria delle notifiche di vulnerabilità.',
    long: "ENISA gestisce la single reporting platform verso cui, ai sensi del CRA, vengono trasmesse le notifiche. Il portale segue i thread di notifica SRP: l'analista inoltra a ENISA e registra ogni step come fatto (con una nota facoltativa), tracciato in audit.",
  },
  {
    term: 'EPSS',
    expansion: 'Exploit Prediction Scoring System',
    short: 'Probabilità stimata che una CVE venga sfruttata a breve.',
    long: 'L’EPSS fornisce, per ogni CVE, una probabilità (0–1) di sfruttamento nei prossimi 30 giorni. Affianca il CVSS nella prioritizzazione: una CVE con EPSS alto merita attenzione anche a parità di severità.',
  },
  {
    term: 'Fornitura',
    expansion: 'Deployment',
    short: 'Installazione di una specifica release presso un cliente e un suo sito.',
    long: 'La fornitura (deployment) collega una release di prodotto a un cliente e, opzionalmente, a un suo sito. È la base per capire “chi ha cosa”: quando una CVE colpisce una release, le forniture indicano quali clienti vanno avvisati.',
  },
  {
    term: 'HBOM',
    expansion: 'Hardware Bill of Materials',
    short: 'Distinta dei componenti hardware di un prodotto.',
    long: 'L’HBOM elenca i componenti hardware (schede, moduli, chip) di un dispositivo. Nel portale viene caricato accanto all’SBOM per i prodotti con componente fisica, così da tracciare anche le vulnerabilità di natura hardware/firmware.',
  },
  {
    term: 'KEV',
    expansion: 'Known Exploited Vulnerabilities',
    short: 'Catalogo di CVE con sfruttamento attivo confermato.',
    long: 'Il catalogo KEV (mantenuto da CISA) elenca le vulnerabilità note per essere sfruttate in attacchi reali. Le CVE marcate KEV hanno priorità massima nel triage e sono evidenziate in dashboard.',
  },
  {
    term: 'Manifest',
    short: 'Stato desiderato del catalogo inviato all’API di sincronizzazione.',
    long: 'Il manifest è il documento (prodotti, clienti, forniture) inviato all’endpoint /sync. Il server valida, calcola il diff rispetto allo stato corrente e applica un piano idempotente di create/update/delete. È il canale di import massivo alternativo alla UI.',
  },
  {
    term: 'Mapping',
    short: 'Associazione esplicita tra prodotto e identificatori CPE/PURL.',
    long: 'Un mapping collega un prodotto a uno o più identificatori (CPE o PURL) usati per il matching delle vulnerabilità. Serve a coprire i casi in cui il solo prefisso CPE del prodotto non basta a intercettare tutte le CVE rilevanti.',
  },
  {
    term: 'Prune',
    short: 'Cancellazione delle entità assenti dal manifest in modalità full.',
    long: 'In sincronizzazione mode “full”, prune:true cancella le entità attive non presenti nel manifest (il manifest diventa autoritativo). Con prune:false quelle entità restano intatte e compaiono solo come warning WOULD_DELETE: un gate di sicurezza contro cancellazioni accidentali.',
  },
  {
    term: 'PURL',
    expansion: 'Package URL',
    short: 'Identificatore standard di un pacchetto software (es. pkg:npm/lodash@4.17.21).',
    long: 'Il Package URL identifica in modo univoco un pacchetto in un ecosistema (npm, maven, pypi…). Come la CPE, è usato per correlare i componenti di un BOM alle vulnerabilità note, con maggiore precisione per le dipendenze software.',
  },
  {
    term: 'Release',
    short: 'Versione specifica di un prodotto, con date di mercato e fine supporto.',
    long: 'Una release è una versione rilasciata di un prodotto (chiave: prodotto + versione). Porta con sé date di ingresso sul mercato e fine supporto, il suffisso CPE, e i componenti/BOM da cui deriva l’analisi delle vulnerabilità.',
  },
  {
    term: 'SBOM',
    expansion: 'Software Bill of Materials',
    short: 'Distinta dei componenti software di un prodotto o release.',
    long: 'L’SBOM (in formato CycloneDX/SPDX) elenca le dipendenze software di un artefatto. È il cuore della gestione delle vulnerabilità: caricato nel portale, consente di sapere quali componenti — e quindi quali CVE — riguardano ogni release.',
  },
  {
    term: 'SLA',
    expansion: 'Service Level Agreement',
    short: 'Scadenza entro cui completare un’azione (triage, notifica, report).',
    long: 'Nel portale gli SLA sono i timer normativi/contrattuali associati alle azioni: es. finestre di notifica CRA (early warning 24h, notifica 72h, relazione finale 14 giorni — 30 giorni dalla notifica 72h per un Severe Incident) o tempi di triage. Registrare uno step ferma il suo timer. La dashboard e le liste evidenziano gli SLA in scadenza per guidare la prioritizzazione.',
  },
  {
    term: 'SRP',
    expansion: 'Security Response Process',
    short: 'Processo strutturato di risposta e comunicazione su una vulnerabilità.',
    long: 'Il report/thread SRP raccoglie note, checklist e comunicazioni relative alla gestione di una o più vulnerabilità, fino alla risoluzione e all’eventuale trasmissione a ENISA. È il fascicolo operativo previsto dal processo di risposta.',
  },
  {
    term: 'Ticket',
    short: 'Unità di lavoro di triage (FKA “Product Triage”) su una o più release.',
    long: 'Un ticket raccoglie le CVE da valutare prima della bozza SRP. Lo crea in automatico il flow notturno quando una release con clienti in campo ha CVE non triagate, oppure l’analista a mano (“Nuovo ticket”) su una o più release, assegnandogli poi le CVE note. L’analista lo prende in carico, aggiunge collaboratori, assegna gli stati VEX e lo promuove a report SRP (una bozza per release) oppure lo chiude.',
  },
  {
    term: 'Tombstone',
    short: 'Marcatura _deleted:true che cancella esplicitamente un’entità nel sync.',
    long: 'In modalità “partial” le cancellazioni avvengono solo via tombstone: si include l’entità nel manifest con _deleted:true. Il record viene soft-deleted; un successivo re-push senza tombstone lo riattiva (reactivate) invece di duplicarlo.',
  },
  {
    term: 'Triage',
    short: 'Valutazione di applicabilità e impatto di una CVE su una release.',
    long: 'Il triage è la decisione, per ogni CVE che tocca un prodotto, se sia realmente sfruttabile nel contesto e con quale priorità. Produce uno stato (es. affected/not affected) e alimenta l’export VEX e i report SRP.',
  },
  {
    term: 'VEX',
    expansion: 'Vulnerability Exploitability eXchange',
    short: 'Documento che dichiara lo stato di sfruttabilità di una CVE per un prodotto.',
    long: 'Il VEX comunica in modo machine-readable se un prodotto è o meno affetto da una data CVE (e perché). Il portale esporta i VEX a partire dagli esiti del triage, così i clienti sanno quali avvisi li riguardano davvero.',
  },
];
